<template>
    <div class="__dashboard_layout">
        <PurgeCssComponent />
        <HeaderComponent v-model="openSidebar" />
        <SidebarComponent
            v-model="openSidebar"
            @current-sidebar="onSetCurrentSidebar($event)"
        />
        <main class="__dashboard_content lg:pl-60">
            <div class="py-6">
                <div class="__container search__header">
                    <div class="__page_name">
                        <span
                            v-if="currentSidebar && currentSidebar.dimension"
                            class="text-gray-900"
                        >
                            {{ currentSidebar.dimension.name || "" }}
                        </span>

                        <div class="spacer" />

                        <span v-if="currentSidebar && currentSidebar.metric">
                            {{ currentSidebar.metric.name }}
                        </span>
                    </div>
                    <div v-if="dateFilter" class="filter-wrapper">
                        <div class="search_params">
                            <span>Filter by date</span>
                            <span
                                class="
                                    pl-4
                                    text-primary
                                    font-medium
                                    cursor-pointer
                                "
                                @click="onClickResetFilter"
                            >
                                Reset filter
                            </span>

                            <div class="__datepicker_container">
                                <div class="datepicker-wrapper">
                                    <DatePickerComponent
                                        id="date_start"
                                        v-bind="datePickerProps"
                                        v-model="date"
                                    />
                                </div>
                                <ButtonComponent
                                    v-bind="buttonProps"
                                    :disabled="isLoading"
                                    @click="getFilteredData"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="__container">
                    <div class="py-4">
                        <div class="rounded-lg">
                            <main>
                                <slot />
                            </main>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import { ref, computed } from "vue";

import {
    DatePickerComponent,
    HeaderComponent,
    SidebarComponent,
} from "@CommonComponents";
import { CalendarIcon } from "@heroicons/vue/solid";
import moment from "moment";
import useAnalytics from "@Functions/useAnalytics.js";

export default {
    name: "AdminLayoutComponent",
    components: {
        HeaderComponent,
        SidebarComponent,
        DatePickerComponent,
    },
    props: {
        dateFilter: { type: Boolean, default: true },
    },

    emits: [ "on-set-current-sidebar" ],
    setup(props, { emit }) {
        const openSidebar = ref(false);
        const { date_start } = window.Subscription;
        const date = ref(new Date(date_start));
        const currentSidebar = ref();

        const {
            isLoading,
            currentRoute,
            store,
            getAnalyticsByTable,
            setupData,
        } = useAnalytics();

        // COMPONENT PROPERTIES

        const datePickerProps = computed(() => {
            return {
                id: "date-picker",
                placeholder: "Select Date",
                minimumView: "month",
                maximumView: "year",
                format: "MMM yyyy",
                calendarButtonIcon: CalendarIcon,
            };
        });

        const buttonProps = computed(() => {
            return {
                type: "primary-btn",
                label: "Filter",
                class: "sm:ml-3 sm:mt-0 mt-3 sm:w-auto ml-0 w-full justify-center",
            };
        });

        const onClickResetFilter = async () => {
            initFilter();

            store.commit("Analytics/setAdminSaveParams", {
                date: moment(date.value).format("YYYY-MM"),
                page: 1,
            });

            await getFilteredData();
        };

        const initFilter = () => {
            date.value = new Date(date_start);
        };

        const getFilteredData = async () => {
            if (!isLoading.value) {
                store.commit("Analytics/setAdminSaveParams", {
                    date: moment(date.value).format("YYYY-MM"),
                    page: 1,
                });

                if (currentRoute.value.name === "admin") {
                    await store.dispatch(`Analytics/getAdminAnalytics`);
                } else {
                    setupData();
                    getAnalyticsByTable();
                }
            }
        };

        const onSetCurrentSidebar = async (val) => {
            emit("on-set-current-sidebar", val);
            currentSidebar.value = val;
        };

        return {
            onSetCurrentSidebar,
            getFilteredData,
            buttonProps,
            datePickerProps,
            date,
            openSidebar,
            isLoading,
            currentSidebar,
            onClickResetFilter,
        };
    },
};
</script>

<style lang="scss">
.__dashboard_layout {
    @apply min-h-screen;
    .__dashboard_content {
        @apply flex-1;
    }
    .__container {
        @apply mx-auto px-8;
    }
    .search__header {
        @apply lg:flex justify-between items-center;
        h1 {
            @apply xl:text-2xl text-xl leading-7 font-bold text-gray-500;
        }
        .filter-wrapper {
            @apply flex sm:justify-end justify-center lg:mt-0 mt-3;
            .search_params {
                min-width: 62px;
                .reset-btn {
                    @apply pl-4 text-primary font-medium cursor-pointer;
                }
            }
        }
    }
    .__datepicker_container {
        @apply flex items-center justify-center mt-1 sm:flex-row flex-col;
        .datepicker-wrapper {
            @apply flex items-center justify-center;
        }
    }
    .__page_name {
        min-width: 50%;
        @apply xl:text-2xl text-xl leading-7 font-bold items-center flex lg:w-1/2 w-full text-gray-500;
        .spacer {
            @apply bg-gray-400;
            height: 0.5px;
            width: 4%;
            transform: rotate(90deg);
        }
    }
}
</style>
